import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SettingField } from "@/components/settings/setting-field";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import type { SaveField } from "../../../hooks/use-auto-save-field";
import type { OnlineBookingConfig } from "../hooks/use-online-booking-config";

interface SelectionModeToggleProps {
  fieldName: "serviceSelectionMode" | "workerSelectionMode";
  onValue: OnlineBookingConfig["serviceSelectionMode" | "workerSelectionMode"];
  offValue: OnlineBookingConfig["serviceSelectionMode" | "workerSelectionMode"];
  i18nFieldKey: string;
  saveField: SaveField<OnlineBookingConfig>;
  saving: boolean;
}

export function SelectionModeToggle({
  fieldName,
  onValue,
  offValue,
  i18nFieldKey,
  saveField,
  saving,
}: SelectionModeToggleProps) {
  const { control } = useFormContext<OnlineBookingConfig>();
  const { t } = useTranslation("settings");

  const prefix = `onlineBooking.fields.${i18nFieldKey}`;

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name={fieldName}
        render={({ field }) => {
          const value = String(field.value ?? offValue);
          const isOn = value === onValue;
          const label = t(`${prefix}.label`);

          return (
            <FormItem>
              <SettingField
                label={label}
                helpText={t(`${prefix}.helpText`)}
                helpDetails={{
                  title: t(`${prefix}.helpDetails.title`),
                  benefits: [
                    t(`${prefix}.helpDetails.modes.off`),
                    t(`${prefix}.helpDetails.modes.on`),
                  ],
                }}
              >
                <div className="flex items-center gap-3">
                  <FormControl>
                    <Switch
                      checked={isOn}
                      disabled={saving}
                      onCheckedChange={(checked) => {
                        const value = checked ? onValue : offValue;
                        void saveField(fieldName, value);
                      }}
                      aria-label={label}
                    />
                  </FormControl>
                  <span className="text-sm text-muted-foreground">
                    {isOn ? t(`${prefix}.state.on`) : t(`${prefix}.state.off`)}
                  </span>
                </div>
              </SettingField>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </div>
  );
}
